import { useRef, useState } from "react";
import { api } from "../api/client";
import { track } from "../lib/analytics";

// The demo lives on the marketing domain; the app doesn't ship a copy of a
// ~20 MB video. preload="none" means zero bytes until the user presses play,
// which matters on a phone connection.
const DEMO_POSTER = "https://ciaotutor.com/demo-poster.jpg";
const DEMO_VIDEO = "https://ciaotutor.com/demo.mp4";

/** What a phone *can* do today — the reason the account is still worth keeping. */
const WORKS_ON_MOBILE = [
  "Exercises built from your own mistakes",
  "Vocabulary you've picked up in sessions",
  "Your dashboard and progress",
  "Your practice plan",
];

interface Props {
  /** Where the capture happened, so the recovery rate can be split by surface. */
  sourcePage: string;
}

/**
 * Shown instead of a dead end when practice sessions are blocked on this device.
 *
 * Voice practice is desktop-only for the foreseeable future (phone speaker and mic
 * share air, so Elena hears herself), so this is the mobile experience, not an
 * apology. It states the limit plainly, shows what the product is, offers a link
 * for later, and points at everything that does work here.
 */
export default function MobileFallback({ sourcePage }: Props) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    setState("sending");
    setError(null);
    try {
      await api.requestMobileLink(email.trim(), sourcePage);
      setState("sent");
      // Closes funnel 5: mobile_blocked → mobile_email_captured. The ratio decides
      // whether mobile-heavy channels are worth pursuing at all.
      track("mobile_email_captured", { source_page: sourcePage });
    } catch (err) {
      setState("idle");
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-sm text-left">
      <div className="relative rounded-lg overflow-hidden border border-border bg-surface aspect-video mb-5">
        <video
          ref={videoRef}
          src={DEMO_VIDEO}
          poster={DEMO_POSTER}
          preload="none"
          controls={playing}
          playsInline
          title="CiaoTutor demo"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {!playing && (
          <button
            type="button"
            onClick={() => { setPlaying(true); videoRef.current?.play(); }}
            aria-label="Play demo video"
            className="absolute inset-0 w-full h-full bg-transparent border-0 cursor-pointer"
          >
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-accent grid place-items-center shadow-lg">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </button>
        )}
      </div>

      <p className="text-[0.875rem] leading-relaxed text-text-secondary mb-5">
        Speaking sessions need a laptop for now. On a phone the speaker and the microphone
        sit centimetres apart, so Elena ends up hearing herself. We'd rather say that than
        ship it broken.
      </p>

      {state === "sent" ? (
        <div className="rounded-lg border border-success/40 bg-success/10 px-4 py-3 mb-5">
          <p className="text-[0.875rem] text-success font-medium mb-0.5">Check your inbox</p>
          <p className="text-[0.8125rem] text-text-secondary leading-relaxed">
            The link is on its way. Reply to it and tell us what you wanted to practise.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mb-5">
          <label htmlFor="mobile-link-email" className="block text-[0.8125rem] text-text-secondary mb-2">
            Send me the link for when I'm at a computer:
          </label>
          <div className="flex gap-2">
            <input
              id="mobile-link-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-base flex-1 min-w-0"
            />
            <button type="submit" className="btn-primary px-4 whitespace-nowrap" disabled={state === "sending"}>
              {state === "sending" ? "…" : "Send"}
            </button>
          </div>
          {error && <p className="mt-2 text-[0.8125rem] text-warning">{error}</p>}
        </form>
      )}

      <div className="border-t border-border pt-4">
        <p className="text-[0.6875rem] uppercase tracking-[0.16em] text-text-tertiary font-medium mb-2">
          Works on your phone right now
        </p>
        <ul className="space-y-1">
          {WORKS_ON_MOBILE.map((item) => (
            <li key={item} className="text-[0.8125rem] text-text-secondary flex gap-2">
              <span className="text-success" aria-hidden="true">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
